import {
  capitalize,
  cloneDeep,
  get,
  isEqual,
  isNil,
  isPlainObject,
} from 'lodash';
import {
  ImmutableTree,
  JsonTree,
  Utils as QbUtils,
} from 'react-awesome-query-builder';
import { distance as levenshteinDistance } from 'fastest-levenshtein';

// Models
import { BlacklistType } from 'app/modules/lists/models';
import {
  EditingRuleFiltersModel,
  RuleStatuses,
  ScenarioConstants,
} from 'app/modules/rules/models';
import { OrgScenarioConfig } from 'app/modules/orgSettings/models';

// Constants
import {
  EXPERIMENTAL_OPERATOR_MIN_INTERVAL_MINUTES,
  SCENARIO_CHECK_OUT_OF_RANGE_NAME,
} from 'app/modules/detectionModels/constants';
import {
  EditingSimpleDetectionModel,
  ScenarioParameters,
  ScenarioPayload,
  TimeWindow,
} from 'app/modules/detectionModels/models';
import { getValidationPayload } from 'app/modules/detectionModels/helpers';
import {
  MONTHLY_TIME_WINDOW_VALUES,
  TIME_WINDOW_OPTIONS,
  WINDOW_STRIDE_OPTIONS,
  WindowStrideOption,
} from 'app/modules/rules/config/timeWindow';
import {
  MINUTE_IN_MILLISECONDS,
  SERIAL_NUMBER_ENTITY_TO_GROUP_TYPE,
  STRIDE_WITH_NO_WINDOW,
} from 'app/modules/rules/constants';
import { getInitialTree } from 'app/modules/rules/components/getInitialTree';
import { MUSTACHE_MATCHER_REGEX } from 'app/shared/constants';
import {
  addDays,
  addMinutes,
  isValid as isValidDate,
  parseISO,
} from 'date-fns';
import { Snapshot } from 'app/modules/rulesAdvanced/types/Snapshot';
import {
  CompositeTypes,
  DmOperator,
  ValueTypes,
  IRSchema,
} from 'app/modules/rulesAdvanced/models';
import { ROUTES_MAP } from 'app/shared/utils/routes';
import { consoleError } from 'app/shared/utils/console';
import { irSchemaContainsExperimentalOperators } from 'app/modules/rulesAdvanced/components/Facts/helpers/helpers';

export const extractMustacheFields = (content: string): string[] => {
  const results: string[] = [];
  MUSTACHE_MATCHER_REGEX.lastIndex = 0;
  let match = MUSTACHE_MATCHER_REGEX.exec(content);
  while (match != null) {
    results.push(match[0].substring(2, match[0].length - 2));
    match = MUSTACHE_MATCHER_REGEX.exec(content);
  }
  return results;
};

export const determineTimePeriodString = (timeWindow: string | undefined) => {
  const { text } = getTimeWindowTextFromValue(timeWindow);
  if (timeWindow === '*') {
    return 'from all time';
  } else if (timeWindow === STRIDE_WITH_NO_WINDOW || !timeWindow) {
    return 'from the specified periods of time';
  }
  return `from the preceding ${text} period`;
};

export function isValidTree(
  queryTree: ImmutableTree,
  achSubtypes: string[] | undefined = undefined,
): boolean {
  return allFieldsValid(QbUtils.getTree(queryTree), achSubtypes);
}

function allFieldsValid(
  queryTree: JsonTree,
  achSubtypes: string[] | undefined = undefined,
): boolean {
  const emptyFields: any[] = [];

  if (queryTree.children1) {
    const itemValues = Object.values(queryTree.children1);
    for (const condition of itemValues) {
      const fieldName: string = get(condition, 'properties.field') || '';
      const fieldOperator: string = get(condition, 'properties.operator') || '';
      const fieldValue: any[] = get(condition, 'properties.value') || [];
      const isCheckingForEmptiness: boolean = [
        'is_empty',
        'is_not_empty',
        'is_null',
        'is_not_null',
      ].includes(fieldOperator);
      const isValid: boolean =
        isCheckingForEmptiness ||
        (fieldValue.length !== 0 && !hasEmptyValue(fieldValue));
      const hasFieldName: boolean = fieldName.length > 0;
      if (
        hasFieldName &&
        isValid &&
        achSubtypes &&
        fieldName.endsWith('u21_ach_risk_score') &&
        (!isAchFieldValid(fieldValue) ||
          !hasAchTxnType(itemValues, achSubtypes))
      ) {
        return false;
      }
      if (
        condition?.type === 'group' &&
        !allFieldsValid(condition, achSubtypes)
      ) {
        return false;
      }

      if (hasFieldName && !isValid) {
        emptyFields.push({
          fieldName,
          fieldValue,
        });
      }
    }
  }
  return emptyFields.length === 0;
}

const hasAchTxnType = (itemValues, achSubtypes: string[]): boolean => {
  // user must first select ach as the type of transactions to flag in a group of conditions
  return itemValues.some(
    (itemValue) =>
      itemValue?.properties?.field === 'txn_event.internal_txn_type' &&
      itemValue?.properties?.operator === 'multiselect_equals' &&
      itemValue?.properties?.value.length === 1 &&
      itemValue?.properties?.value[0] !== undefined &&
      itemValue?.properties?.value[0].every((value) =>
        achSubtypes.includes(value),
      ),
  );
};

const isAchFieldValid = (fieldValue): boolean =>
  fieldValue.every((scoreVal) => scoreVal >= 0 && scoreVal <= 100);

function hasEmptyValue(fieldValue: string[] | object) {
  if (Array.isArray(fieldValue)) {
    return fieldValue.some((currValue) => {
      if (Array.isArray(currValue)) {
        return hasEmptyValue(currValue);
      }

      if (typeof currValue === 'object') {
        return recursivelyCheckIfAnyValueIsEmpty(currValue);
      }

      return isNil(currValue);
    });
  }

  if (typeof fieldValue === 'object') {
    return recursivelyCheckIfAnyValueIsEmpty(fieldValue);
  }

  return isNil(fieldValue);
}

function recursivelyCheckIfAnyValueIsEmpty(currObject: object) {
  if (isNil(currObject)) {
    return true;
  }

  return Object.keys(currObject).some((key) => {
    if (typeof currObject[key] === 'object') {
      return recursivelyCheckIfAnyValueIsEmpty(currObject[key]);
    }

    if (Array.isArray(currObject[key])) {
      return hasEmptyValue(currObject[key]);
    }

    return isNil(currObject[key]);
  });
}

export const windowCountWithDates = (
  execStartDatetime: Date,
  windowStride: string,
  execEndDatetime?: Date,
) => {
  const startTimeInMilliseconds = execStartDatetime.getTime();
  const endTimeInMilliseconds =
    execEndDatetime && execEndDatetime.getTime() < Date.now()
      ? execEndDatetime.getTime()
      : Date.now();
  const durationInMinutes =
    (endTimeInMilliseconds - startTimeInMilliseconds) / MINUTE_IN_MILLISECONDS;

  const windowStrideInMinutes = windowStrideStrToMinutes(windowStride);
  return Math.floor(durationInMinutes / windowStrideInMinutes) + 1;
};

export const calculateWindowEnd = (
  execStartDatetime: string,
  windowSize: string | undefined,
): Date => {
  return addMinutes(
    new Date(execStartDatetime),
    getTimeWindowTextFromValue(windowSize).minutes,
  );
};

export const addDaysToDate = (date: Date, daysToAdd: number): Date => {
  return addDays(date, daysToAdd);
};

export const timeWindowStrIsMonthly = (timeWindow: string): boolean => {
  const found = TIME_WINDOW_OPTIONS.find((e) => e.value === timeWindow);
  if (!found || !found.value) {
    return false;
  }
  return MONTHLY_TIME_WINDOW_VALUES.includes(found.value);
};

export const windowStrideStrToMinutes = (windowStride: string): number => {
  const found = WINDOW_STRIDE_OPTIONS.find((e) => e.value === windowStride);
  if (!found) {
    return 0;
  }
  return found.minutes;
};

export const getDefaultStrideForTimeWindow = (timeWindow: string): string => {
  const found = TIME_WINDOW_OPTIONS.find((e) => e.value === timeWindow);
  if (!found) {
    return TIME_WINDOW_OPTIONS[0].defaultstride;
  }
  return found.defaultstride;
};

export const getTimeWindowTextFromValue = (
  timeWindowValue: string | undefined,
): TimeWindow => {
  const returnWindow = {
    key: '',
    value: '',
    text: '',
    minutes: 0,
    defaultstride: '',
  };

  if (timeWindowValue === STRIDE_WITH_NO_WINDOW) {
    returnWindow.text = STRIDE_WITH_NO_WINDOW;
    return returnWindow;
  }

  const found = TIME_WINDOW_OPTIONS.find((e) => e.value === timeWindowValue);
  if (!found) {
    return returnWindow;
  }
  return found;
};

// so. this is a bit of a hack for world remit
// currently multiple occurrences is capable of running any rule, regardless of status
// we're doing this to satisfy this conversation https://unit21chat.slack.com/archives/C01UFHHPHRP/p1634310833355800
export const constructRulesSelectorStatuses = (
  scenarioName: string | null,
): RuleStatuses[] => {
  const statuses: RuleStatuses[] = [
    RuleStatuses.ACTIVE,
    RuleStatuses.VALIDATION,
  ];

  if (scenarioName === 'multiple_occurrences') {
    statuses.push(RuleStatuses.ARCHIVED);
  }

  return statuses;
};

export const getScenarioDefaultValues = (
  baseScenarioConfig: any,
  orgScenarioConfig: OrgScenarioConfig,
): ScenarioParameters => {
  const parameterOptions = baseScenarioConfig.parameter_options;
  const orgScenarioConfigValues = orgScenarioConfig.values || {};
  const { content } = baseScenarioConfig;
  const defaultValues: ScenarioParameters = {};

  MUSTACHE_MATCHER_REGEX.lastIndex = 0;
  let match = MUSTACHE_MATCHER_REGEX.exec(content);
  while (match != null) {
    const key = match[0].substring(2, match[0].length - 2);
    switch (key) {
      case ScenarioConstants.TIME_WINDOW: {
        let timeWindowStr;
        if (
          key in parameterOptions &&
          'default_value' in parameterOptions[key]
        ) {
          timeWindowStr = parameterOptions[key].default_value;
        } else {
          timeWindowStr =
            TIME_WINDOW_OPTIONS[TIME_WINDOW_OPTIONS.length - 1].value;
        }
        defaultValues[ScenarioConstants.TIME_WINDOW] = timeWindowStr;
        defaultValues.$window_stride =
          getDefaultStrideForTimeWindow(timeWindowStr);
        break;
      }
      case ScenarioConstants.NUMERIC_COMPARISON:
        break; // No default values set for $numeric_comparison
      default:
        if (
          key in parameterOptions &&
          'default_value' in parameterOptions[key] &&
          parameterOptions[key].field_type === 'multi_select'
        ) {
          defaultValues[key] = parameterOptions[key].default_value;
        } else if (
          key in parameterOptions &&
          'default_value' in parameterOptions[key]
        ) {
          const defaultValue = Array.isArray(
            parameterOptions[key].default_value,
          )
            ? parameterOptions[key].default_value
            : parameterOptions[key].default_value.toString();
          defaultValues[key] = defaultValue;
        } else if (
          key in parameterOptions &&
          'org_values' in parameterOptions[key] &&
          parameterOptions[key].org_values in orgScenarioConfigValues
        ) {
          // eslint-disable-next-line prefer-destructuring
          defaultValues[key] =
            orgScenarioConfigValues[parameterOptions[key].org_values][0];
        } else if (
          `${baseScenarioConfig.name}:${key}` in orgScenarioConfigValues
        ) {
          // eslint-disable-next-line prefer-destructuring
          defaultValues[key] =
            orgScenarioConfigValues[`${baseScenarioConfig.name}:${key}`][0];
        } else if (
          key in parameterOptions &&
          parameterOptions[key].field_type === 'dropdown' &&
          parameterOptions[key].values &&
          parameterOptions[key].values.length > 0
        ) {
          // eslint-disable-next-line prefer-destructuring
          defaultValues[key] = parameterOptions[key].values[0];
        } else if (
          key in parameterOptions &&
          parameterOptions[key].field_type === 'dropdown' &&
          parameterOptions[key].key_values &&
          parameterOptions[key].key_values.length > 0
        ) {
          // eslint-disable-next-line prefer-destructuring
          defaultValues[key] = parameterOptions[key].key_values[0];
          if (
            isPlainObject(defaultValues[key]) &&
            Object.prototype.hasOwnProperty.call(defaultValues[key], 'value')
          ) {
            // key_values can be an array of objects with 'key', 'text' and 'value', use the 'value' property
            defaultValues[key] = defaultValues[key].value;
          }
        }
    }
    match = MUSTACHE_MATCHER_REGEX.exec(content);
  }
  return defaultValues;
};

export const changeScenarioParameter = (
  scenario: ScenarioPayload,
  key: string,
  val: any,
) => {
  const selectedScenario = cloneDeep(scenario);

  if (selectedScenario.parameters[key] === 'between' && val !== 'between') {
    delete selectedScenario.parameters[
      `${key.replace('-comparator', '')}-extra_value`
    ];
  }

  // For serial_number_out_of_range, set or clear group_type based on entity selection
  // This ensures the backend uses the correct model (Entity vs Instrument)
  if (scenario.name === SCENARIO_CHECK_OUT_OF_RANGE_NAME && key === 'entity') {
    const normalizedVal =
      typeof val === 'string' ? val.toLowerCase() : String(val);
    if (normalizedVal in SERIAL_NUMBER_ENTITY_TO_GROUP_TYPE) {
      selectedScenario.parameters.group_type =
        SERIAL_NUMBER_ENTITY_TO_GROUP_TYPE[normalizedVal];
    } else {
      // Clear stale group_type if entity doesn't map to a known value
      delete selectedScenario.parameters.group_type;
    }
  }

  return {
    ...selectedScenario,
    parameters: {
      ...selectedScenario.parameters,
      [key]: val,
    },
    embeddedFilters: {
      ...selectedScenario.embeddedFilters,
      [key]: getEmptyFilters(),
    },
  };
};
export const changeScenarioParameterOnly = (
  scenario: ScenarioPayload,
  key: string,
  val: any,
): ScenarioPayload => {
  const selectedScenario = cloneDeep(scenario);

  return {
    ...selectedScenario,
    parameters: {
      ...selectedScenario.parameters,
      [key]: val,
    },
  };
};

export const getEmptyFilters = (): EditingRuleFiltersModel => ({
  raw_sql: '',
  query_tree: QbUtils.loadTree(getInitialTree()),
  inclusion_tags: [],
  exclusion_tags: [],
  inclusion_tag_names: [],
  exclusion_tag_names: [],
  aggregate_query_tree: {},
});

export const ONLY_USERS_FIELD_KEY = 'ONLYUSERKEYS';

export function getFieldNamesFromKey(
  key: string,
  blacklistType: BlacklistType | undefined,
): string[] {
  // key looks like `txn_events_1`, `entity.id`, `instrument.id_2`,
  // returned fieldName must exist on QueryBuilderConfig.fields
  const tableAndFieldName = key.replace(/_\d+$/, '');
  const entityFields = ['business', 'entity', 'user'];
  const userFields = ['entity', 'user'];

  // Try to obtain the main field if we have a field.property => entity.id would apply to entity
  // TODO do we really want to do this? Maybe filter subfields from qbConfig
  const [tableName, fieldName] = tableAndFieldName.split('.');
  let filteredTable: string[];

  switch (tableName) {
    case 'txn_events':
      filteredTable = ['txn_event'];
      break;
    case 'entity':
      if (fieldName === ONLY_USERS_FIELD_KEY) {
        filteredTable = userFields;
      } else {
        filteredTable = entityFields;
      }
      break;
    case 'instrument':
      filteredTable = ['txn_instrument'];
      break;
    case 'transaction':
      filteredTable = ['txn_event'];
      break;
    default:
      filteredTable = [tableName];
  }

  if (fieldName && filteredTable.includes('txn_event')) {
    filteredTable = getTableFromFieldName(fieldName) || filteredTable;
  }

  return filterByBlacklistType(filteredTable);

  function getTableFromFieldName(name: string): string[] | null {
    if (name.includes('entity')) return entityFields;
    if (name.includes('instrument')) return ['txn_instrument'];
    return null;
  }

  function filterByBlacklistType(filteredTableX: string[]): string[] {
    if (
      ['BUSINESS', 'USER'].includes(String(blacklistType)) &&
      filteredTableX === entityFields
    ) {
      return blacklistType ? [blacklistType.toLowerCase()] : [];
    }
    return filteredTableX;
  }
}

/**
 * This doesn't have to be exact. It's just used to get a rough range.
 */
const BREAKDOWN_TO_MINUTES = {
  min: 1,
  h: 60,
  d: 24 * 60,
  w: 24 * 60 * 7,
  m: 24 * 60 * 31,
  y: 24 * 60 * 365,
};

export const convertCustomTimeWindowToMinutes = (
  timeWindow: string,
): number => {
  const [, timeNumStr, timeUnit] = timeWindow.split(/(\d+)/); // this actually returns ["", "17", "w"]
  const timeNumber = parseInt(timeNumStr, 10);
  if (isNaN(timeNumber)) {
    throw new Error(`Invalid time window: ${timeWindow}`);
  }
  return BREAKDOWN_TO_MINUTES[timeUnit] * timeNumber;
};

// timeWindow -> (exclusiveOptionLimit, inclusiveOptionLimit]
// timeWindow is an earlier selected time from constant TIME_WINDOW_OPTIONS
// where "exclusiveOptionLimit" is an option that is just greater than 10 minutes and an arbitruary buffer
// and "inclusiveOptionLimit" is an option less than or equal to the initially selected time window
export const getWindowStrideOptions = (
  timeWindow: string,
  minMinutes: number = 10,
  additionalIntervalOptions: string[] = [],
  irSchema?: IRSchema,
): WindowStrideOption[] => {
  const timeWindowInMinutes = convertCustomTimeWindowToMinutes(timeWindow);

  // Enforce 1-day (1440 minutes) minimum when AI_FUZZY_SCORE is present
  let effectiveMinMinutes = minMinutes;
  if (irSchema) {
    if (irSchemaContainsExperimentalOperators(irSchema)) {
      effectiveMinMinutes = Math.max(
        minMinutes,
        EXPERIMENTAL_OPERATOR_MIN_INTERVAL_MINUTES,
      );
    }
  }

  return WINDOW_STRIDE_OPTIONS.filter((opt) => {
    const isTheOptionLongerThan10Minutes = opt.minutes >= effectiveMinMinutes;
    const isMonthlyOrNotRequired =
      timeWindowStrIsMonthly(timeWindow) ||
      opt.monthly_window_required === undefined;

    const exclusiveOptionLimit =
      (opt.minutes > timeWindowInMinutes / 30 &&
        isTheOptionLongerThan10Minutes) ||
      isTheOptionLongerThan10Minutes;

    const inclusiveOptionLimit = opt.minutes <= timeWindowInMinutes;

    return (
      (exclusiveOptionLimit &&
        inclusiveOptionLimit &&
        isMonthlyOrNotRequired) ||
      additionalIntervalOptions.includes(opt.value)
    );
  });
};

export const getInitialSimpleEditingModel = (
  parentModel: EditingSimpleDetectionModel,
  dataMigrationLag: number,
): EditingSimpleDetectionModel => {
  const newScenarioPayload = cloneDeep(parentModel);

  newScenarioPayload.validationPayload = getValidationPayload(
    newScenarioPayload.metadata,
    newScenarioPayload.validationPayload,
    dataMigrationLag,
  );

  return newScenarioPayload;
};

const OperatorFunctionMap: Record<
  DmOperator,
  (...args: any[]) => boolean | number | string | null
> = {
  MUL: (a: number | null, b: number | null) => {
    if (a === null || b === null) return null;
    return a * b;
  },
  DIV: (a: number | null, b: number | null) => {
    if (a === null || b === null) return null;
    return a / b;
  },
  SUB: (a: number | null, b: number | null) => {
    if (a === null || b === null) return null;
    return a - b;
  },
  ADD: (a: number | null, b: number | null) => {
    if (a === null || b === null) return null;
    return a + b;
  },
  AND: (a: Array<boolean> | null) => {
    if (a === null || !Array.isArray(a)) return null;
    return a.every((v) => v);
  },
  OR: (a: Array<boolean> | null) => {
    if (a === null || !Array.isArray(a)) return null;
    return a.some((v) => v);
  },
  GT: (a: unknown, b: unknown) => {
    if (a === null || b === null) return null;
    return typeof a === 'number' && typeof b === 'number' && a > b;
  },
  LT: (a: unknown, b: unknown) => {
    if (a === null || b === null) return null;
    return typeof a === 'number' && typeof b === 'number' && a < b;
  },
  LE: (a: unknown, b: unknown) => {
    if (a === null || b === null) return null;
    return typeof a === 'number' && typeof b === 'number' && a <= b;
  },
  GE: (a: unknown, b: unknown) => {
    if (a === null || b === null) return null;
    return typeof a === 'number' && typeof b === 'number' && a >= b;
  },
  EQ: (a: unknown, b: unknown) => isEqual(a, b),
  ANY_EQ: (a: unknown, b: unknown) => OperatorFunctionMap.EQ(a, b),
  NEQ: (a: unknown, b: unknown) => !OperatorFunctionMap.EQ(a, b),
  NONE_EQ: (a: unknown, b: unknown) => !OperatorFunctionMap.EQ(a, b),
  IN: (a: unknown, b: unknown) => {
    if (a === null && b === null) return null;
    if (a === null || b === null) return false;
    return (
      (Array.isArray(b) && b.includes(a)) || (Array.isArray(a) && a.includes(b))
    );
  },
  ANY_IN: (a: unknown, b: unknown) => OperatorFunctionMap.IN(a, b),
  NOT_IN: (a: unknown, b: unknown) => {
    const result = OperatorFunctionMap.IN(a, b);
    return result === null ? null : !result;
  },
  NONE_IN: () => null,
  LIKE: (a: unknown, b: unknown) => {
    if (a === null || b === null) return null;
    return typeof a === 'string' && typeof b === 'string' && a.includes(b);
  },
  ILIKE: (a: unknown, b: unknown) => OperatorFunctionMap.LIKE(a, b),
  ANY_LIKE: (a: unknown, b: unknown) => OperatorFunctionMap.LIKE(a, b),
  NOT_LIKE: (a: unknown, b: unknown) => {
    const result = OperatorFunctionMap.LIKE(a, b);
    return result === null ? null : !result;
  },
  NOT_ILIKE: (a: unknown, b: unknown) => {
    const result = OperatorFunctionMap.LIKE(a, b);
    return result === null ? null : !result;
  },
  NONE_LIKE: (a: unknown, b: unknown) => {
    const result = OperatorFunctionMap.LIKE(a, b);
    return result === null ? null : !result;
  },
  BETWEEN: (
    a: string | number | null,
    b: string | number | null,
    c: string | number | null,
  ) => {
    if (a === null || b === null || c === null) return null;
    return a >= b && a < c;
  },
  IS: (a: unknown) => a === null,
  IS_EMPTY: () => null,
  IS_NOT_EMPTY: () => null,
  ANY_IS_EMPTY: () => null,
  NONE_IS_EMPTY: () => null,
  ILIKE_CONTAINS: (a: string | null, b: string | null) => {
    if (a === null || b === null) return null;
    return a.toLowerCase().includes(b.toLowerCase());
  },
  NOT_ILIKE_CONTAINS: (a: string | null, b: string | null) => {
    const result = OperatorFunctionMap.ILIKE_CONTAINS(a, b);
    return result === null ? null : !result;
  },
  STARTS_WITH: (a: string | null, b: string | null) => {
    if (a === null || b === null) return null;
    return a.startsWith(b);
  },
  NOT_STARTS_WITH: (a: string | null, b: string | null) => {
    const result = OperatorFunctionMap.STARTS_WITH(a, b);
    return result === null ? null : !result;
  },
  ENDS_WITH: (a: string | null, b: string | null) => {
    if (a === null || b === null) return null;
    return a.endsWith(b);
  },
  NOT_ENDS_WITH: (a: string | null, b: string | null) => {
    const result = OperatorFunctionMap.ENDS_WITH(a, b);
    return result === null ? null : !result;
  },
  LOWERCASE: (a: string | null) => {
    if (a === null) return null;
    return a.toLowerCase();
  },
  UPPERCASE: (a: string | null) => {
    if (a === null) return null;
    return a.toUpperCase();
  },
  TRIM: (a: string | null) => {
    if (a === null) return null;
    return a.trim();
  },
  CAPITALIZE: (a: string | null) => {
    if (a === null) return null;
    return capitalize(a);
  },
  ABS: (a: number | null) => {
    if (a === null) return null;
    return Math.abs(a);
  },
  SQRT: (a: number | null) => {
    if (a === null) return null;
    return Math.sqrt(a);
  },
  EXP: (a: number | null, b: number | null) => {
    if (a === null || b === null) return null;
    return a ** b;
  },
  MODULO: (a: number | null, b: number | null) => {
    if (a === null || b === null) return null;
    return a % b;
  },
  LN: () => null,
  LOG: (a: number | null) => {
    if (a === null) return null;
    return Math.log(a);
  },
  ISNT: () => null,
  NOT: (a: unknown) => !a,
  CONTAINS: () => null,
  LEVENSHTEIN_DIVIDED: () => null,
  FUZZY_SCORE: (a: string, b: string) => {
    if (a === null || b === null) return null;
    const denominator = Math.max(a.length, b.length);
    if (denominator === 0) return 1;
    return 1 - levenshteinDistance(a, b) / denominator;
  },
  AI_FUZZY_SCORE: () => null,
  SANITIZE: (a: string | null) => {
    if (a === null) return null;
    return a.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
  },
};

// in seconds
const DURATION_MAP = {
  MINUTE: 60,
  HOUR: 60 * 60,
  DAY: 60 * 60 * 24,
  WEEK: 60 * 60 * 24 * 7,
  MONTH: 60 * 60 * 24 * 31,
  YEAR: 60 * 60 * 24 * 365,
};
/**
 * Attaches 'snapshot_value' property to VARIABLES
 * @param condition (from rule content)
 * @param snapshot (from alert hit content)
 * @returns condition with 'snapshot_values' attached to VARIABLE types
 */
export const enrich = (condition, snapshot: Snapshot) => {
  if (condition === null) {
    return null;
  }

  const localCondition = cloneDeep(condition);
  if (localCondition.type === CompositeTypes.RULE_CONDITION_COMPOSITE) {
    localCondition.operands = localCondition.operands.map((operand) =>
      enrich(operand, snapshot),
    );
  } else if (
    'name' in localCondition &&
    snapshot[localCondition.name] !== undefined
  ) {
    localCondition.snapshot_value = snapshot[localCondition.name].value;
  }

  return localCondition;
};

const getValues = (operands, windowEnd?: string) => {
  const values: Array<any> = [];

  operands.forEach((operand) => {
    if (
      [
        'ADD',
        'SUB',
        'MUL',
        'DIV',
        'ABS',
        'LOG',
        'EXP',
        'SQRT',
        'FUZZY_SCORE',
        'AI_FUZZY_SCORE',
        'UPPERCASE',
        'LOWERCASE',
        'TRIM',
        'CAPITALIZE',
      ].includes(operand.operator)
    ) {
      values.push(
        OperatorFunctionMap[operand.operator](
          ...getValues(operand.operands, windowEnd),
        ),
      );
    } else if ('snapshot_value' in operand) {
      if (isValidDate(parseISO(operand.snapshot_value))) {
        values.push(parseISO(operand.snapshot_value).getTime());
      } else {
        values.push(operand.snapshot_value);
      }
    } else if (
      operand.type === ValueTypes.DURATION &&
      windowEnd &&
      isValidDate(parseISO(windowEnd))
    ) {
      values.push(
        parseISO(windowEnd).getTime() -
          operand.value * DURATION_MAP[operand.unit] * 1000,
      );
    } else if (isValidDate(parseISO(operand.value))) {
      values.push(parseISO(operand.value).getTime());
    } else if (
      operand.type === ValueTypes.CONSTANT &&
      typeof operand.value === 'string' &&
      (operand.value === 'true' || operand.value === 'false')
    ) {
      // the case for legacy conditions where booleans are stored as strings
      values.push(operand.value === 'true');
    } else {
      values.push(operand.value);
    }
  });

  return values;
};

export const evaluate = (condition, windowEnd?: string) => {
  if (condition === null) {
    return null;
  }

  const localCondition = cloneDeep(condition);
  if (
    localCondition.type === CompositeTypes.RULE_CONDITION_COMPOSITE &&
    ['AND', 'OR'].includes(localCondition.operator)
  ) {
    try {
      localCondition.operands = localCondition.operands.map((operand) =>
        evaluate(operand, windowEnd),
      );
      localCondition.evaluation_result = OperatorFunctionMap[
        localCondition.operator
      ](localCondition.operands.map((operand) => operand.evaluation_result));
    } catch (e) {
      consoleError(e);
      localCondition.evaluation_result = null;
    }
  } else if (
    localCondition.type === CompositeTypes.RULE_CONDITION_COMPOSITE &&
    localCondition.operator in OperatorFunctionMap
  ) {
    try {
      localCondition.evaluation_result = OperatorFunctionMap[
        localCondition.operator
      ](...getValues(localCondition.operands, windowEnd));
    } catch (e) {
      consoleError(e);
      localCondition.evaluation_result = null;
    }
  } else if (localCondition.type === ValueTypes.TAG) {
    localCondition.evaluation_result = null;
  }

  return localCondition;
};

export const evaluateAndEnrich = (
  ruleCondition,
  snapshot: Snapshot,
  windowEnd?: string,
) => evaluate(enrich(ruleCondition, snapshot), windowEnd);

export const getRouteToRule = (
  ruleId: number,
  hasReadRulesPermission: boolean,
  ruleStatus: RuleStatuses,
) => {
  if (!hasReadRulesPermission) {
    return undefined;
  } else if (ruleStatus === RuleStatuses.VALIDATION) {
    return ROUTES_MAP.rulesValidationId.path.replace(':id', String(ruleId));
  }
  return ROUTES_MAP.rulesId.path.replace(':id', String(ruleId));
};
